import { useCallback, useEffect, useRef, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";

import { useAuth } from "@/components/AuthProvider";
import {
  fetchHubPresenceCurrent,
  HUB_PRESENCE_ACTIVE_MS,
  hubPresenceCurrentQueryKey,
  postHubPresenceHeartbeat,
  postHubPresenceUpdate,
  sendHubPresenceCloseBeacon,
  type HubPresenceCurrentResponse,
  type HubPresenceState,
} from "@/lib/hubPresenceApi";
import { resolveHubPresenceRoute } from "@/lib/hubPresenceRoute";
import { hubPresenceHttpUpdateIsNeeded } from "@/lib/hubPresenceHttpUpdate";
import { applyHubNotificationsUnreadSseEvent } from "@/lib/notificationsUnreadCountLive";
import { applyHubPresenceTabStreamConnectedSnapshot } from "@/lib/hubPresenceTabStreamSnapshot";
import { HUB_CACHE_CHANNEL } from "@/lib/hubCacheChannels";
import { useHubCacheStream } from "@/lib/useHubCacheStream";
import { getHubTabId } from "@/lib/hubTabId";
import {
  getHubPresenceTabLastPublished,
  resetHubPresenceTabSessionOnLogout,
  runHubPresenceTabBootPost,
  setHubPresenceTabLastPublished,
} from "@/lib/hubPresenceTabSession";
import {
  createPresenceUpdateCoordinator,
  type PresenceUpdateCoordinator,
} from "@/lib/presenceUpdateCoordinator";
import { useHubPresenceAttention } from "@/lib/useHubPresenceAttention";
import { registerSgsLauncherExitHandler } from "@/lib/sgsLauncherExit";

const IDLE_CHECK_MS = 30_000;
const PRESENCE_HEARTBEAT_MS = 60_000;
const CROSS_TAB_REFRESH_FALLBACK_MS = 1_500;

type PresenceRefreshLockManager = {
  request: (
    name: string,
    options: { ifAvailable: true },
    callback: (lock: unknown | null) => Promise<void>,
  ) => Promise<void>;
};

/**
 * Global Hub page-presence tracker.
 * - POST on first paint, real navigation, visibility, and idle/active transitions
 * - Periodic POST heartbeat for liveness (no per-tab EventSource)
 * - Page presence and unread notifications ride the browser-wide live socket
 */
export function HubPresenceProvider(props: { children: ReactNode }): JSX.Element {
  const { children } = props;
  const { session } = useAuth();
  const location = useLocation();
  const queryClient = useQueryClient();

  const activityDirtyRef = useRef(false);
  const lastInteractionRef = useRef(Date.now());
  const lastPublishedRef = useRef<{
    routeKey: string;
    isVisible: boolean;
    presenceState: HubPresenceState;
  } | null>(null);
  const forcePresenceRowRef = useRef(false);
  const presencePhaseRef = useRef<HubPresenceState>("active");
  const accessTokenRef = useRef<string | null>(null);
  const wasEnabledRef = useRef(false);
  const locationRef = useRef(location);
  const routeRefreshTimerRef = useRef<number | undefined>(undefined);
  const broadcastRef = useRef<BroadcastChannel | null>(null);
  const refreshAbortControllersRef = useRef(new Set<AbortController>());
  const refreshFallbackTimersRef = useRef(new Set<number>());
  locationRef.current = location;

  accessTokenRef.current = session?.access_token ?? null;

  const enabled = session != null && session.user?.id != null && session.user.id !== "";
  const authenticatedUserId = session?.user?.id ?? null;
  const authenticatedUserIdRef = useRef<string | null>(authenticatedUserId);
  authenticatedUserIdRef.current = authenticatedUserId;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const sendLatestStateRef = useRef<() => Promise<void>>(async () => undefined);
  sendLatestStateRef.current = async (): Promise<void> => {
    if (!enabledRef.current) {
      return;
    }
    const activitySinceLastHeartbeat = activityDirtyRef.current;
    activityDirtyRef.current = false;
    const currentLocation = locationRef.current;
    const route = resolveHubPresenceRoute(currentLocation.pathname, currentLocation.search);
    const isVisible =
      typeof document === "undefined" ? true : document.visibilityState !== "hidden";
    const next = {
      routeKey: route.routeKey,
      isVisible,
      presenceState: presencePhaseRef.current,
    };
    const lastPublished =
      lastPublishedRef.current ?? getHubPresenceTabLastPublished();
    if (
      !hubPresenceHttpUpdateIsNeeded({
        lastPublished,
        next,
        force: forcePresenceRowRef.current,
      })
    ) {
      lastPublishedRef.current = lastPublished;
      return;
    }
    const publish = async (): Promise<void> => {
      if (getHubPresenceTabLastPublished() != null && forcePresenceRowRef.current !== true) {
        lastPublishedRef.current = getHubPresenceTabLastPublished();
        return;
      }
      const updated = await postHubPresenceUpdate({
        tabId: getHubTabId(),
        route,
        isVisible,
        activitySinceLastHeartbeat,
        presenceState: presencePhaseRef.current,
      });
      if (Array.isArray(updated.viewers) && authenticatedUserIdRef.current != null) {
        queryClient.setQueryData(
          hubPresenceCurrentQueryKey(authenticatedUserIdRef.current, updated.routeKey),
          { routeKey: updated.routeKey, viewers: updated.viewers },
        );
      }
      lastPublishedRef.current = next;
      setHubPresenceTabLastPublished(next);
      forcePresenceRowRef.current = false;
    };
    try {
      if (lastPublished == null && forcePresenceRowRef.current !== true) {
        await runHubPresenceTabBootPost(publish);
        return;
      }
      await publish();
    } catch (error) {
      activityDirtyRef.current =
        activityDirtyRef.current || activitySinceLastHeartbeat;
      throw error;
    }
  };

  const coordinatorRef = useRef<PresenceUpdateCoordinator | null>(null);

  const requestUpdate = useCallback(async (): Promise<void> => {
    if (coordinatorRef.current == null) {
      coordinatorRef.current = createPresenceUpdateCoordinator({
        run: () => sendLatestStateRef.current(),
      });
    }
    await coordinatorRef.current.request();
  }, []);

  useEffect(() => {
    const userId = authenticatedUserId;
    if (!enabled || userId == null || typeof BroadcastChannel === "undefined") {
      return;
    }
    const channel = new BroadcastChannel(`hub-presence-current:${userId}`);
    broadcastRef.current = channel;
    channel.onmessage = (message: MessageEvent<unknown>): void => {
      const payload = message.data;
      if (payload == null || typeof payload !== "object" || Array.isArray(payload)) {
        return;
      }
      const record = payload as {
        type?: unknown;
        routeKey?: unknown;
        data?: unknown;
      };
      if (
        record.type !== "presence-current" ||
        typeof record.routeKey !== "string" ||
        record.data == null ||
        typeof record.data !== "object"
      ) {
        return;
      }
      const data = record.data as HubPresenceCurrentResponse;
      if (
        authenticatedUserIdRef.current !== userId ||
        data.routeKey !== record.routeKey ||
        !Array.isArray(data.viewers)
      ) {
        return;
      }
      queryClient.setQueryData(
        hubPresenceCurrentQueryKey(userId, record.routeKey),
        data,
      );
    };
    return () => {
      if (broadcastRef.current === channel) {
        broadcastRef.current = null;
      }
      channel.close();
    };
  }, [authenticatedUserId, enabled, queryClient]);

  useEffect(
    () => () => {
      for (const controller of refreshAbortControllersRef.current) {
        controller.abort();
      }
      refreshAbortControllersRef.current.clear();
      for (const timer of refreshFallbackTimersRef.current) {
        window.clearTimeout(timer);
      }
      refreshFallbackTimersRef.current.clear();
    },
    [authenticatedUserId],
  );

  useEffect(() => {
    void queryClient.removeQueries({
      queryKey: ["hub-presence"],
      predicate: (query) => query.queryKey[1] !== authenticatedUserId,
    });
  }, [authenticatedUserId, queryClient]);

  const refreshCurrentRoute = useCallback((): void => {
    if (routeRefreshTimerRef.current != null) {
      return;
    }
    routeRefreshTimerRef.current = window.setTimeout(() => {
      routeRefreshTimerRef.current = undefined;
      const currentLocation = locationRef.current;
      const route = resolveHubPresenceRoute(currentLocation.pathname, currentLocation.search);
      const expectedUserId = authenticatedUserIdRef.current;
      if (expectedUserId == null) {
        return;
      }
      const queryKey = hubPresenceCurrentQueryKey(expectedUserId, route.routeKey);
      const previousUpdatedAt = queryClient.getQueryState(queryKey)?.dataUpdatedAt ?? 0;
      const fetchAndShare = async (): Promise<void> => {
        if (authenticatedUserIdRef.current !== expectedUserId) {
          return;
        }
        const controller = new AbortController();
        refreshAbortControllersRef.current.add(controller);
        try {
          const data = await fetchHubPresenceCurrent(route.routeKey, controller.signal);
          if (authenticatedUserIdRef.current !== expectedUserId) {
            return;
          }
          queryClient.setQueryData(queryKey, data);
          broadcastRef.current?.postMessage({
            type: "presence-current",
            routeKey: route.routeKey,
            data,
          });
        } finally {
          refreshAbortControllersRef.current.delete(controller);
        }
      };
      const scheduleFallback = (): void => {
        if (authenticatedUserIdRef.current !== expectedUserId) {
          return;
        }
        const timer = window.setTimeout(() => {
          refreshFallbackTimersRef.current.delete(timer);
          if (authenticatedUserIdRef.current !== expectedUserId) {
            return;
          }
          const updatedAt = queryClient.getQueryState(queryKey)?.dataUpdatedAt ?? 0;
          if (updatedAt <= previousUpdatedAt) {
            void fetchAndShare().catch(() => undefined);
          }
        }, CROSS_TAB_REFRESH_FALLBACK_MS);
        refreshFallbackTimersRef.current.add(timer);
      };
      const lockManager = (
        navigator as Navigator & { locks?: PresenceRefreshLockManager }
      ).locks;
      if (lockManager == null) {
        void fetchAndShare().catch(() => undefined);
        return;
      }
      void lockManager
        .request(
          `hub-presence-current:${expectedUserId}:${route.routeKey}`,
          { ifAvailable: true },
          async (lock) => {
            if (lock != null) {
              await fetchAndShare();
            }
          },
        )
        .then(scheduleFallback, scheduleFallback);
    }, 500);
  }, [queryClient]);

  useEffect(
    () => () => {
      coordinatorRef.current?.dispose();
      coordinatorRef.current = null;
      if (routeRefreshTimerRef.current != null) {
        window.clearTimeout(routeRefreshTimerRef.current);
      }
    },
    [],
  );

  const markAttention = useCallback((): void => {
    activityDirtyRef.current = true;
    lastInteractionRef.current = Date.now();
    if (presencePhaseRef.current === "idle") {
      presencePhaseRef.current = "active";
      void requestUpdate();
    }
  }, [requestUpdate]);

  useHubPresenceAttention(markAttention, enabled);

  const presenceRouteKey = resolveHubPresenceRoute(location.pathname, location.search).routeKey;

  useHubCacheStream({
    channels:
      authenticatedUserId != null
        ? [
            HUB_CACHE_CHANNEL.hubPagePresenceAll,
            HUB_CACHE_CHANNEL.hubPagePresenceRoute(presenceRouteKey),
            HUB_CACHE_CHANNEL.hubNotificationsUnread(authenticatedUserId),
          ]
        : [],
    enabled,
    connectionKey: authenticatedUserId,
    onEvent: (event) => {
      const connected = applyHubPresenceTabStreamConnectedSnapshot(
        queryClient,
        authenticatedUserId,
        event,
      );
      if (connected.appliedUnread || connected.appliedViewers) {
        return;
      }
      if (applyHubNotificationsUnreadSseEvent(queryClient, event)) {
        return;
      }
      if (event.page_presence_reason != null) {
        const eventRouteKey = event.page_presence_route_key;
        const eventViewers = event.page_presence_viewers;
        if (typeof eventRouteKey === "string" && Array.isArray(eventViewers)) {
          const currentRoute = resolveHubPresenceRoute(
            locationRef.current.pathname,
            locationRef.current.search,
          );
          if (eventRouteKey === currentRoute.routeKey && authenticatedUserId != null) {
            queryClient.setQueryData(
              hubPresenceCurrentQueryKey(authenticatedUserId, eventRouteKey),
              { routeKey: eventRouteKey, viewers: eventViewers },
            );
          }
          return;
        }
        if (event.channel === HUB_CACHE_CHANNEL.hubPagePresenceAll) {
          return;
        }
        refreshCurrentRoute();
      }
    },
  });

  // Initial update and route / auth changes.
  useEffect(() => {
    if (!enabled) {
      return;
    }
    presencePhaseRef.current = "active";
    lastInteractionRef.current = Date.now();
    activityDirtyRef.current = true;
    void requestUpdate();
  }, [enabled, location.pathname, location.search, session?.user?.id]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const timer = window.setInterval(() => {
      const currentLocation = locationRef.current;
      const route = resolveHubPresenceRoute(currentLocation.pathname, currentLocation.search);
      const isVisible =
        typeof document === "undefined" ? true : document.visibilityState !== "hidden";
      void postHubPresenceHeartbeat({
        tabId: getHubTabId(),
        route,
        isVisible,
        activitySinceLastHeartbeat: false,
        presenceState: presencePhaseRef.current,
      })
        .then((updated) => {
          if (!Array.isArray(updated.viewers) || authenticatedUserIdRef.current == null) {
            return;
          }
          queryClient.setQueryData(
            hubPresenceCurrentQueryKey(authenticatedUserIdRef.current, updated.routeKey),
            { routeKey: updated.routeKey, viewers: updated.viewers },
          );
        })
        .catch(() => undefined);
    }, PRESENCE_HEARTBEAT_MS);
    return () => window.clearInterval(timer);
  }, [enabled, session?.user?.id]);

  // Visibility (not window focus — focus was posting presence/update without a route change).
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const onVisibility = (): void => {
      if (document.visibilityState === "visible") {
        markAttention();
      }
      void requestUpdate();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled, markAttention, requestUpdate]);

  // Active → idle transitions (and back).
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const timer = window.setInterval(() => {
      const isActive = Date.now() - lastInteractionRef.current <= HUB_PRESENCE_ACTIVE_MS;
      const phase = isActive ? "active" : "idle";
      if (phase === presencePhaseRef.current) {
        return;
      }
      presencePhaseRef.current = phase;
      if (phase === "idle") {
        activityDirtyRef.current = false;
      }
      void requestUpdate();
    }, IDLE_CHECK_MS);
    return () => window.clearInterval(timer);
  }, [enabled, requestUpdate]);

  // Best-effort close on pagehide and tablet launcher Exit (WebView often skips pagehide).
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const closePresence = (): void => {
      sendHubPresenceCloseBeacon({
        tabId: getHubTabId(),
        accessToken: accessTokenRef.current,
      });
    };
    const onPageHide = (): void => {
      closePresence();
    };
    window.addEventListener("pagehide", onPageHide);
    const unregisterLauncherExit = registerSgsLauncherExitHandler(closePresence);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      unregisterLauncherExit();
    };
  }, [enabled]);

  useEffect(() => {
    if (enabled) {
      wasEnabledRef.current = true;
      return;
    }
    if (!wasEnabledRef.current) {
      return;
    }
    wasEnabledRef.current = false;
    lastPublishedRef.current = null;
    forcePresenceRowRef.current = false;
    resetHubPresenceTabSessionOnLogout();
    sendHubPresenceCloseBeacon({
      tabId: getHubTabId(),
      accessToken: accessTokenRef.current,
    });
    accessTokenRef.current = null;
    void queryClient.removeQueries({ queryKey: ["hub-presence"] });
  }, [enabled, queryClient]);

  return <>{children}</>;
}
